// features/subscription/CreditBalance.js
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '../../context/SubscriptionContext';
import { Dialog, Listbox, Transition } from '@headlessui/react';
import { Fragment } from 'react';

const CreditBalance = () => {
  const { t } = useTranslation();
  const {
    creditBalance,
    creditTransactions,
    purchaseCredits,
    refreshCreditTransactions,
    loading,
    error
  } = useSubscription();
  
  const [purchaseAmount, setPurchaseAmount] = useState(100);
  const [paymentMethodId, setPaymentMethodId] = useState('');
  const [processingPurchase, setProcessingPurchase] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Refresh transaction history when component mounts
  useEffect(() => {
    const refreshData = async () => {
      try {
        if (isRefreshing) return;
        
        setIsRefreshing(true);
        console.log('Refreshing credit transactions');
        await refreshCreditTransactions();
      } catch (error) {
        console.error('Error refreshing credit transactions:', error);
      } finally {
        setIsRefreshing(false);
      }
    };
    
    refreshData();
  }, []);
  
  // Define credit package options
  const creditPackages = [
    { amount: 100, price: 10 },
    { amount: 300, price: 25 },
    { amount: 500, price: 40 },
    { amount: 1000, price: 75 }
  ];
  
  const handleAmountChange = (e) => {
    setPurchaseAmount(parseInt(e.target.value, 10));
    setPurchaseError(null);
  };
  
  const handlePaymentMethodChange = (e) => {
    setPaymentMethodId(e.target.value);
    setPurchaseError(null);
  };
  
  const handlePurchase = (e) => {
    e.preventDefault();
    
    if (purchaseAmount <= 0) {
      setPurchaseError(t('subscription.invalidAmount'));
      return;
    }
    
    if (!paymentMethodId) {
      setPurchaseError(t('subscription.paymentMethodRequired'));
      return;
    }
    
    // Open confirmation modal instead of purchasing immediately
    setIsConfirmModalOpen(true);
  };
  
  const confirmPurchase = async () => {
    try {
      setProcessingPurchase(true);
      setPurchaseError(null);
      setIsConfirmModalOpen(false);
      
      await purchaseCredits(purchaseAmount, paymentMethodId);
      
      setPurchaseSuccess(true);
      setPurchaseAmount(100);
      setPaymentMethodId('');
    } catch (err) {
      setPurchaseError(err.message || t('subscription.purchaseFailed'));
    } finally {
      setProcessingPurchase(false);
    }
  };
  
  // Calculate the price based on the selected amount
  const getPrice = (amount) => {
    const pkg = creditPackages.find(p => p.amount === amount);
    return pkg ? pkg.price : (amount * 0.1); // Default to $0.10 per credit
  };

  // Format date properly or return a fallback message
  const formatDate = (dateString) => {
    if (!dateString) return t('subscription.unavailable');
    
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return t('subscription.unavailable');
      }
      return date.toLocaleDateString();
    } catch (error) {
      return t('subscription.unavailable');
    }
  };

  // Get transaction type label
  const getTransactionTypeLabel = (source) => {
    if (!source) return t('subscription.unknown');
    
    switch(source) {
      case 'purchase':
        return t('subscription.purchase');
      case 'subscription':
        return t('subscription.subscription');
      case 'inpaint':
        return t('subscription.inpaint');
      case 'search-replace':
        return t('subscription.searchReplace');
      case 'search-recolor':
        return t('subscription.searchRecolor');
      case 'refund':
        return t('subscription.refund');
      case 'adjustment':
        return t('subscription.adjustment');
      default:
        return source;
    }
  };

  // Format transaction amount with sign
  const formatAmount = (transaction) => {
    if (!transaction || transaction.amount === undefined) return '0';
    
    const amount = Number(transaction.amount);
    if (isNaN(amount)) return '0';
    
    // If amount is already negative, don't add a sign
    // If amount is positive, add a plus sign
    return amount < 0 ? amount.toString() : `+${amount}`;
  };
  
  // Calculate running balance for each transaction
  const transactionsWithBalance = useMemo(() => {
    if (!creditTransactions || creditTransactions.length === 0) return [];
    
    // Sort transactions by created_at date, newest first
    const sortedTransactions = [...creditTransactions].sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });
    
    let runningBalance = creditBalance;
    
    // Calculate running balance for each transaction
    return sortedTransactions.map((transaction, index) => {
      const amount = Number(transaction.amount);
      
      // For the first transaction (most recent), use the current balance
      if (index === 0) {
        return { ...transaction, calculatedBalance: runningBalance };
      }
      
      // For subsequent transactions, subtract the amount from the running balance
      // (since we're going backwards in time)
      runningBalance -= amount;
      
      return { ...transaction, calculatedBalance: runningBalance };
    });
  }, [creditTransactions, creditBalance]);
  
  if (loading && creditBalance === null) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" aria-label={t('common.loading')}></div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-8 text-center dark:text-white">{t('subscription.creditBalance')}</h1>
      
      {error && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {purchaseError && (
        <div className="bg-red-100 dark:bg-red-900 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-200 px-4 py-3 rounded mb-4">
          {purchaseError}
        </div>
      )}
      
      {purchaseSuccess && (
        <div className="bg-green-100 dark:bg-green-900 border border-green-400 dark:border-green-700 text-green-700 dark:text-green-200 px-4 py-3 rounded mb-4">
          {t('subscription.purchaseSuccess')}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border dark:border-gray-700 mb-6">
            <h2 className="text-2xl font-bold mb-4 dark:text-white">{t('subscription.currentBalance')}</h2>
            <div className="text-5xl font-bold text-primary-500 dark:text-primary mb-2">
              {creditBalance || 0}
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              {t('subscription.creditsAvailable')}
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border dark:border-gray-700">
            <h2 className="text-2xl font-bold mb-4 dark:text-white">{t('subscription.purchaseCredits')}</h2>
            
            <form onSubmit={handlePurchase}>
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2" htmlFor="credit-amount">
                  {t('subscription.selectAmount')}
                </label>
                <Listbox value={purchaseAmount} onChange={(value) => handleAmountChange({ target: { value } })}>
                  <div className="relative mt-1">
                    <Listbox.Button className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-left">
                      <span className="block truncate">
                        {purchaseAmount} {t('subscription.credits')} - ${getPrice(purchaseAmount).toFixed(2)}
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {creditPackages.map((pkg) => (
                          <Listbox.Option
                            key={pkg.amount}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                active ? 'bg-primary-light dark:bg-primary-dark text-primary-900 dark:text-primary-light' : 'text-gray-900 dark:text-white'
                              }`
                            }
                            value={pkg.amount}
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {pkg.amount} {t('subscription.credits')} - ${pkg.price.toFixed(2)}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-600 dark:text-primary">
                                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 dark:text-gray-300 font-medium mb-2" htmlFor="payment-method">
                  {t('subscription.paymentMethod')}
                </label>
                {(() => {
                  const paymentMethods = [
                    { id: '', label: t('subscription.selectPaymentMethod') },
                    { id: 'card_1', label: t('subscription.paymentMethods.visa') },
                    { id: 'card_2', label: t('subscription.paymentMethods.mastercard') }
                  ];
                  const selectedMethod = paymentMethods.find(method => method.id === paymentMethodId) || paymentMethods[0];
                  return (
                    <Listbox value={selectedMethod} onChange={(option) => handlePaymentMethodChange({ target: { value: option.id } })}>
                      <div className="relative mt-1">
                        <Listbox.Button className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-left">
                          <span className="block truncate">{selectedMethod.label}</span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {paymentMethods.map((option) => (
                              <Listbox.Option
                                key={option.id}
                                className={({ active }) =>
                                  `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                    active ? 'bg-primary-light dark:bg-primary-dark text-primary-900 dark:text-primary-light' : 'text-gray-900 dark:text-white'
                                  }`
                                }
                                value={option}
                                disabled={option.id === ''}
                              >
                                {({ selected }) => (
                                  <>
                                    <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                      {option.label}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary-600 dark:text-primary">
                                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  );
                })()}
              </div>
              
              <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600 mb-6">
                <div className="flex justify-between mb-2">
                  <span className="font-medium dark:text-gray-300">{t('subscription.amount')}</span>
                  <span className="dark:text-gray-300">{purchaseAmount} {t('subscription.credits')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium dark:text-gray-300">{t('subscription.price')}</span>
                  <span className="dark:text-gray-300">${getPrice(purchaseAmount).toFixed(2)}</span>
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-opacity-50 dark:bg-primary-dark dark:hover:bg-primary-dark"
                disabled={processingPurchase}
              >
                {processingPurchase ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('common.processing')}
                  </span>
                ) : t('subscription.purchase')}
              </button>
            </form>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 border dark:border-gray-700">
          <h2 className="text-2xl font-bold mb-4 dark:text-white">{t('subscription.transactionHistory')}</h2>
          
          {transactionsWithBalance && transactionsWithBalance.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('subscription.date')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('subscription.type')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('subscription.amount')}
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      {t('subscription.balance')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {transactionsWithBalance.map((transaction, index) => (
                    <tr key={transaction.id || index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {getTransactionTypeLabel(transaction.source)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatAmount(transaction)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {transaction.calculatedBalance !== undefined ?
                          transaction.calculatedBalance :
                          '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-600 dark:text-gray-400">{t('subscription.noTransactions')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      <Transition appear show={isConfirmModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsConfirmModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/30 dark:bg-black/50" aria-hidden="true" />
          </Transition.Child>
          
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transform transition-all">
                <Dialog.Title className="text-xl font-bold mb-4 dark:text-white">
                  {t('subscription.confirmPurchase')}
                </Dialog.Title>
                
                <div className="mb-6">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    {t('subscription.confirmPurchaseMessage', {
                      amount: purchaseAmount,
                      price: getPrice(purchaseAmount).toFixed(2)
                    })}
                  </p>
                  
                  <div className="p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-gray-300">{t('subscription.amount')}</span>
                      <span className="dark:text-gray-300">{purchaseAmount} {t('subscription.credits')}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-gray-300">{t('subscription.price')}</span>
                      <span className="dark:text-gray-300">${getPrice(purchaseAmount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium dark:text-gray-300">{t('subscription.paymentMethod')}</span>
                      <span className="dark:text-gray-300">
                        {paymentMethodId === 'card_1'
                          ? t('subscription.paymentMethods.visa')
                          : paymentMethodId === 'card_2'
                            ? t('subscription.paymentMethods.mastercard')
                            : t('subscription.selectPaymentMethod')}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex space-x-4">
                  <button
                    className="flex-1 py-2 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200"
                    onClick={() => setIsConfirmModalOpen(false)}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    className="flex-1 py-2 px-4 bg-primary hover:bg-primary-dark text-white rounded dark:bg-primary-dark dark:hover:bg-primary-dark"
                    onClick={confirmPurchase}
                  >
                    {t('common.confirm')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default CreditBalance;
